// Copyright (c) StrangeBee 2023

import React, { ReactElement, useState, useEffect, useMemo, useContext } from 'react';
import DashboardPanel from '@Organisms/Dashboard/DashboardPanel/DashboardPanel';
import WidgetRowList from '@Organisms/Dashboard/DashboardDetails/WidgetRowList/WidgetRowList';
import { DashboardOutput, WidgetChartType, WidgetOutput } from 'thehive-sdk';
import { DashboardContext, WidgetQuery } from '@Context/DashboardContext';
import { Prompt } from 'react-router-dom';
import { useLocalMessage } from '@Helpers/';
import { DashboardEventList } from '@Organisms/Dashboard/eventList';
import { useGetSettings } from '@Queries/UISettingsQueries';
import { WidgetError } from '../DashboardDetails/WidgetError';
import { useQueries } from 'react-query';
import { createWidgetQueryKey } from '@Molecules/Widgets/utils';
import { widgetQueries } from './constants';
import { useListAllFields } from '@Queries/describeQueries';
import { OrgSwitcherContext } from '@Context/OrgSwitcherProvider';

import './DashboardView.scss';

interface DashboardViewProps {
    dashboardData: DashboardOutput;
    isFetching: boolean;
    eventList: DashboardEventList;
    refetch: () => void;
}

export const DashboardView = ({ dashboardData, isFetching, eventList, refetch }: DashboardViewProps): ReactElement => {
    const i = useLocalMessage();

    const [dashboard, setDashboard] = useState<DashboardOutput>(dashboardData);
    const [editMode, setEditMode] = useState(false);
    const { data: settingsData, isFetched: isSettingsFetched } = useGetSettings();
    const { disallowPeriodAllInDashboards = false, disableCacheOption = true } = settingsData || {};
    const { data: describeData } = useListAllFields();
    const { setBlockOrgSwitch } = useContext(OrgSwitcherContext);

    useEffect(() => {
        if (!isFetching) {
            setDashboard(dashboardData);
        }
    }, [dashboardData, isFetching]);

    useEffect(() => {
        window.onbeforeunload = editMode ? (): boolean | null => true : null;

        if (editMode) {
            setBlockOrgSwitch(true);
        }

        return () => {
            window.onbeforeunload = null;
            setBlockOrgSwitch(false);
        };
    }, [editMode, setBlockOrgSwitch]);

    const widgetsFlattened = useMemo(
        () => dashboard?.definition.items.reduce((acc, { items }) => [...acc, ...items], [] as WidgetOutput[]) || [],
        [dashboard],
    );

    const queries = useQueries(
        widgetsFlattened.map(({ id, type, options }) => ({
            queryFn: (key: any) => {
                const keyOptions = key.queryKey[1];

                return widgetQueries[type as WidgetChartType]?.({
                    options: keyOptions,
                    definition: { ...dashboard.definition, cache: disableCacheOption || dashboard.definition.cache },
                    describeData,
                });
            },
            queryKey: [
                createWidgetQueryKey({ id, options, type }),
                options,
                dashboard.definition.period,
                dashboard.definition.customPeriod,
                disableCacheOption || dashboard.definition.cache,
            ],
            enabled: !!dashboard && isSettingsFetched,
        })),
    );

    const widgets: WidgetQuery[] = useMemo(
        () =>
            widgetsFlattened.map((widget, index) => ({
                widget,
                query: queries[index],
            })),
        [widgetsFlattened, queries],
    );

    return (
        <div className="dashboard-view">
            <DashboardContext.Provider value={{ dashboard, setDashboard, editMode, setEditMode, widgets }}>
                <Prompt when={editMode} message={() => i('app.general.beforeunload')} />
                <div className="dashboard-view-header">
                    <DashboardPanel eventList={eventList} refetch={refetch} />
                </div>
                <div className="dashboard-view-content" id="dashboard-view-content">
                    {dashboardData?.definition.period === 'all' && disallowPeriodAllInDashboards ? (
                        <WidgetError message={i('dashboard.disallow-all')} multiple />
                    ) : (
                        <WidgetRowList />
                    )}
                </div>
            </DashboardContext.Provider>
        </div>
    );
};
